import notifications from '@imt/vue-admin-menus/src/store/notifications';
import {asString} from '@imt/vue-list/src/utils/queryParams';
import toolbox from '@imt/vue-toolbox/src/store';
import utils, {axiosWithAuth, deepCase} from '@imt/vue-toolbox/src/utils';
import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

export const authedAxios = axiosWithAuth(),
    resourceHosts = {
        class_code: {
            host: process.env.VUE_APP_ADMIN_URL_FORMS,
            adminPath: (psId, id) => `admin/policy-system/${psId}/class-codes/${id}`,
            path: 'class-codes',
        },
        datamap: {
            host: process.env.VUE_APP_ADMIN_URL_MAPPING,
            adminPath: (psId, id) => `admin/datamaps/${id}`,
            path: 'datamaps',
        },
        form: {
            host: process.env.VUE_APP_ADMIN_URL_FORMS,
            adminPath: (psId, id) => `admin/policy-system/${psId}/forms/${id}`,
            path: 'forms',
        },
        page: {
            host: process.env.VUE_APP_ADMIN_URL_CMS,
            adminPath: (psId, id) => `admin/policy-systems/${psId}/edit/pages/${id}`,
            path: 'pages',
        },
        process: {
            host: process.env.VUE_APP_BEHANDLE_URL,
            adminPath: (psId, id) => `admin/processes/${id}`,
            path: 'processes',
        },
        rule: {
            host: process.env.VUE_APP_ADMIN_URL_BUSINESS_RULES,
            adminPath: (psId, id) => `admin/policy-system/${psId}/rules/${id}`,
            path: 'rules',
        },
    };

export const getters = {};

export const actions = {
    async deleteSet(_, id) {
        await authedAxios.delete(`/sets/${id}/`);
    },
    async exportSet({state}, items) {
        const response = await authedAxios.post(
            `/sets/${state.set.id}/export/`,
            utils.dataFormatter.serialize({
                stuff: {
                    type: 'Set',
                    items: deepCase(items, 'snake'),
                },
            }),
            { responseType: 'blob' },
        );

        return window.URL.createObjectURL(new Blob([response.data]));
    },
    async fetchActivity(context, {
        filters = {},
        limit = null,
        page = null,
    }) {
        let url = `${process.env.VUE_APP_ADMIN_URL_USERS}/api/v1/activity/?include=user`;

        url += `${Object.keys(filters).map(k => `&filter[${k}]=${filters[k]}`).join('')}`;
        url += page ? `&page=${page}` : '';
        url += limit ? `&page[size]=${limit}` : '';

        const response = await authedAxios.get(url);

        let activity = utils.dataFormatter.deserialize(response.data),
            paging = response.data.meta.pagination;

        paging.pageSize = response.data.data.length;
        paging.links = response.data.links;

        return {
            activity,
            paging,
        };
    },
    async fetchResources(_, {resourceType, setId}) {
        const {host, path} = resourceHosts[resourceType],
            response = await authedAxios.get(`${host}/api/v1/${path}/?include=versions&filter[vinna_set_id]=${setId}`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchSet({commit}, id) {
        const response = await authedAxios.get(`/sets/${id}/?include=created_by,updated_by`);

        commit('SET_DATA', {
            field: 'set',
            data: utils.dataFormatter.deserialize(response.data),
        });

        return response;
    },
    async fetchSets({commit, state}, {queryObject = {}} = {}) {
        const queryString = asString(queryObject),
            response = await authedAxios.get(`/sets/?${queryString}`);

        commit('SET_DATA', {
            field: 'sets',
            data: utils.dataFormatter.deserialize(response.data).filter(set => state.showDeleted ? true : !set.deletedAt),
        });

        commit('SET_DATA', {field: 'totalItemCount', data: response.data.meta.pagination.count});

        return response;
    },
    async importSet({commit}, setFile) {
        const formData = new FormData();

        formData.append('file', setFile);
        const response = await authedAxios.post(`/sets/import/`, formData),
            formattedData = utils.dataFormatter.deserialize(response.data);

        commit('SET_DATA', {
            field: 'set',
            data: formattedData,
        });

        return formattedData;
    },
    async saveSet({commit}, data) {
        let response = await authedAxios[data.id ? 'patch' : 'post'](
                `/sets/${data.id ? `${data.id}/` : ''}?include=created_by,updated_by`,
                utils.dataFormatter.serialize({
                    stuff: {
                        ...data,
                        type: 'Set',
                    },
                }),
            ),
            formattedData = {};

        formattedData = utils.dataFormatter.deserialize(response.data);
        commit('SET_DATA', {
            field: 'set',
            data: formattedData,
        });

        return formattedData;
    },
    async fetchPolicySystem(context, policySystemId) {
        let response = await authedAxios.get(`${process.env.VUE_APP_ADMIN_URL_CMS}/api/v1/policy-systems/${policySystemId}/`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchPolicies(context, policyNumber) {
        let response = await authedAxios.get(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policies/?filter[number]=${policyNumber}&include=versions&sort=-versions__term_effective_date`);

        return utils.dataFormatter.deserialize(response.data);
    },
};

export const mutations = {
    CLEAR_SEARCH_FIELDS(state) {
        state.searchFields = {};
    },
    SET_DATA(state, {field, data}) {
        state[field] = data;
    },
};

export const state = () => {
    return {
        searchFields: {},
        set: {},
        sets: [],
        showDeleted: false,
        totalItemCount: 0,
        pageHeaderTitle: '',
    };
};

export default new Vuex.Store({
    modules: {
        notifications,
        toolbox,
    },
    actions,
    getters,
    mutations,
    state: state(),
});
